
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import FeaturedTherapists from "@/components/FeaturedTherapists";
import FeaturedServices from "@/components/FeaturedServices";
import PartnerOrganizations from "@/components/PartnerOrganizations";
import Testimonials from "@/components/Testimonials";
import ComparisonSection from "@/components/ComparisonSection";
import PortalButtons from "@/components/PortalButtons";
import Footer from "@/components/Footer";
import WellnessConcierge from "@/components/service-selection/WellnessConcierge";

import { useFeaturedPractitioners } from "@/hooks/useFeaturedPractitioners";
import { OrganizationSEO } from "@/components/seo/OrganizationSEO";
import { Helmet } from "react-helmet-async";
import { useCallback } from "react";

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Book a Wellness Practitioner on Resonow",
  "description": "Find and book a certified wellness or clinical practitioner through Resonow's guided booking flow.",
  "step": [
    { "@type": "HowToStep", "name": "Choose Service Type", "text": "Select between crisis resources, clinical therapy, or wellness services based on your needs." },
    { "@type": "HowToStep", "name": "Select Healing Stage", "text": "Choose your healing stage and effective domain to find the right type of support." },
    { "@type": "HowToStep", "name": "Find a Practitioner", "text": "Browse certified practitioners filtered by specialty, location, language, and availability." },
    { "@type": "HowToStep", "name": "Book and Pay", "text": "Select an available time slot, choose your session format, and complete secure payment." }
  ]
};

const Index = () => {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const { practitioners, loading, error } = useFeaturedPractitioners();

  const handleStageRecommended = useCallback((stage: number) => {
    navigate(`/service-selection?stage=${stage}`);
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <OrganizationSEO />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Helmet>
      <Navbar />
      <main className="flex-grow">
        <HeroSection />

        {/* AI Wellness Concierge */}
        <section className="py-12 bg-brand-100/60">
          <div className="container mx-auto max-w-6xl px-4">
            <WellnessConcierge onStageRecommended={handleStageRecommended} />
          </div>
        </section>
        
        {loading ? (
          <section className="py-12 bg-white">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-display font-semibold text-warm-800 mb-8 text-center">
                {t('featuredPractitionersTitle')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-48 bg-gray-200 rounded animate-pulse"
                  ></div>
                ))}
              </div>
            </div>
          </section>
        ) : error ? (
          <section className="py-12 bg-white">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-display font-semibold text-warm-800 mb-8 text-center">
                {t('featuredPractitionersTitle')}
              </h2>
              <div className="text-center text-warm-500">
                <p>{t('loadError')}</p>
              </div>
            </div>
          </section>
        ) : (
          <FeaturedTherapists therapists={practitioners} />
        )}

        <FeaturedServices />
        <PartnerOrganizations />
        <Testimonials />
        <ComparisonSection />
        <PortalButtons />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
